import { ALL, ReverseCoverageTypesCodes } from '../common/Constants';
import { ConstantsLagoon } from '../common/ConstantsLagoon';
import { useCoverageType } from '../hooks/useCoverageType';
import { useFeatureFlag } from '../hooks/useFeatureFlag';
import { StoreKeys } from '../hooks/useStore/state';
import { useStore } from '../hooks/useStore/useStore';
import { FeatureInclusion } from '../models/Lagoon';
import {
  getCurrentMember,
  getPlanVariationCode,
  getReportingCode,
  padPolicyId,
} from './user.utils';

export const returnSuppressFlag = (featureDnt: string) => {
  const { suppressionLagoonData, memberPolicy: storeMemberPolicy } = useStore(
    StoreKeys.UI_FEATURE_SUPPRESSION
  );

  const currentMember = getCurrentMember();
  const coverageType = useCoverageType();
  const memberReportingCode = getReportingCode(currentMember, coverageType);
  const memberPlanVariationCode = getPlanVariationCode(
    currentMember,
    coverageType
  );
  const memberPVRC = memberPlanVariationCode?.concat(memberReportingCode);
  const costEstimatesFlag =
    ConstantsLagoon.FEATURE_SUPPRESSION_FLAGS.COST_ESTIMATES;
  const [featureSuppressionFlag] = useFeatureFlag([
    ConstantsLagoon.FEATURE_FLAGS.FEATURE_SUPPRESSION,
  ]);

  const suppressFeature = suppressionLagoonData?.find((item) => {
    const policyIdsArray = item.policyIds
      .split(',')
      .map((id) => id.trim().padStart(7, '0'));
    const isPolicyIncluded =
      policyIdsArray.includes(ALL) ||
      policyIdsArray.includes(storeMemberPolicy?.padStart(7, '0'));
    let ispvrcCodeIncluded = true;
    if (item.DNT_feature === costEstimatesFlag && item.pvrcCodes) {
      const pvrcCodesArray = item.pvrcCodes
        .split(',')
        .map((code) => code.trim());
      ispvrcCodeIncluded = pvrcCodesArray.includes(memberPVRC);
    }
    return (
      item.DNT_feature === featureDnt && isPolicyIncluded && ispvrcCodeIncluded
    );
  })?.active;
  return !!(featureSuppressionFlag && suppressFeature);
};

export const suppressCostEstimateFromCostTab = (policyID: string) => {
  const { suppressionLagoonData } = useStore(StoreKeys.UI_FEATURE_SUPPRESSION);
  const costEstimateDnt =
    ConstantsLagoon.FEATURE_SUPPRESSION_FLAGS
      .SUPPRESS_COST_ESTIMATES_FROM_COST_TAB;

  const suppressCostEstimate = suppressionLagoonData?.find((item) => {
    const policyIdsArray = item?.policyIds?.split(',') || [];
    return (
      item?.DNT_feature === costEstimateDnt &&
      policyIdsArray.includes(policyID) &&
      item?.active
    );
  })?.active;

  return !!suppressCostEstimate;
};

export const suppressCostEstimatesForPolicies = (
  coverageType,
  costEstimateDnt,
  costEstimateEmpireDnt
) => {
  const suppressCostEstimate = returnSuppressFlag(costEstimateDnt);
  const suppressCostEstimateEmpireDnt = returnSuppressFlag(
    costEstimateEmpireDnt
  );
  const suppressCostEstimateEmpire =
    coverageType !== ReverseCoverageTypesCodes.MEDICAL &&
    suppressCostEstimateEmpireDnt;

  return suppressCostEstimate || suppressCostEstimateEmpire;
};

export const returnInclusionFlag = (
  featureDnt,
  canSitusStateBeEmpty = true
) => {
  const {
    includeLagoonData,
    memberPolicy: storeMemberPolicy,
    situsState: storeMemberSitusState,
  } = useStore(StoreKeys.UI_FEATURE_INCLUSION) || {};
  const [featureInclusionFlag] = useFeatureFlag([
    ConstantsLagoon.FEATURE_FLAGS.FEATURE_INCLUSION,
  ]);
  const includeFeature = includeLagoonData?.find((item: FeatureInclusion) => {
    const isPolicyIncluded = item.policyIds.includes(storeMemberPolicy);
    const isSitusStateIncluded = item?.situsState
      ? item.situsState.includes(storeMemberSitusState)
      : canSitusStateBeEmpty;
    return (
      item.dntFeature === featureDnt && isPolicyIncluded && isSitusStateIncluded
    );
  })?.active;

  return !!(featureInclusionFlag && includeFeature);
};

export const useSuppressRRVirtualCareOnCEXWidget = (
  coverageType,
  policyNumber
) => {
  const { suppressionLagoonData } = useStore(StoreKeys.UI_FEATURE_SUPPRESSION);
  const [enableRRVirtualCareCEXWidget] = useFeatureFlag([
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_RR_VIRTUAL_CARE_CEX_WIDGET,
  ]);

  // Extract and split policy IDs for the relevant DNT features
  const extractPolicyIds = (featureFlag) =>
    (suppressionLagoonData || [])
      .filter((item) => item.DNT_feature === featureFlag)
      .flatMap((item) => item.policyIds.split(','));

  const rrUnetPolicyIds = extractPolicyIds(
    ConstantsLagoon.FEATURE_SUPPRESSION_FLAGS.VIRTUAL_CARE_UNET_RAILROAD
  );

  const rrBehavioralVcPolicyIds = extractPolicyIds(
    ConstantsLagoon.FEATURE_SUPPRESSION_FLAGS.VIRTUAL_CARE_UNET_QUICK_CARE
  );

  // Format the policy number if the feature flag is enabled
  const formattedPolicyNumber = enableRRVirtualCareCEXWidget
    ? padPolicyId(policyNumber)
    : policyNumber;

  // Check suppression conditions
  return (
    (coverageType !== ReverseCoverageTypesCodes['BEHAVIORAL HEALTH'] &&
      rrUnetPolicyIds.includes(formattedPolicyNumber)) ||
    (coverageType === ReverseCoverageTypesCodes['BEHAVIORAL HEALTH'] &&
      rrBehavioralVcPolicyIds.includes(formattedPolicyNumber))
  );
};
